import { FeatureFace } from '../FeatureFace';
import { FeatureSurface } from '../FeatureSurface';

/**
 * 拓扑面代理创建输入。
 *
 * 设计说明：
 * 1. 当前构建链路不能解析 `interface extends SomeType[0]` 这种继承写法；
 * 2. 使用类型交叉表达同样的结构，避免引入运行时逻辑变化。
 */
export type CreateTopologyFeatureFaceProxyInput = ConstructorParameters<typeof FeatureFace>[0] & {
  /** 底层曲面类型；缺失时表示当前快照没有解析到解析曲面语义。 */
  surfaceKind?: TopologySurfaceKind;
};

/**
 * 判断当前拓扑面是否必须暴露为解析曲面对象。
 *
 * 设计说明：
 * 1. `FeatureFace` 是平面面的标准交互对象，配合、沿实体移动、参考平面都依赖这个边界；
 * 2. `FeatureSurface` 只代表圆柱、圆锥、样条面等非平面解析曲面；
 * 3. surfaceKind 缺失时不应猜成曲面，否则普通网格拓扑会被误判为不可作为平面参考。
 *
 * @param surfaceKind 底层曲面类型。
 * @returns 是否需要创建 `FeatureSurface`。
 */
function shouldCreateFeatureSurface(surfaceKind: TopologySurfaceKind | undefined): boolean {
  return typeof surfaceKind === 'string' && surfaceKind !== 'plane';
}

/**
 * 根据权威拓扑面语义创建特征面代理。
 *
 * @param input 面代理创建输入。
 * @returns 平面返回 `FeatureFace`，非平面解析曲面返回 `FeatureSurface`。
 */
export function createTopologyFeatureFaceProxy(input: CreateTopologyFeatureFaceProxyInput): FeatureFace {
  if (shouldCreateFeatureSurface(input.surfaceKind)) {
    return new FeatureSurface({
      ...input,
      surfaceKind: input.surfaceKind,
    });
  }

  /*
   * 平面必须保持为基础 FeatureFace。
   * 这能让类型边界与 SolidWorks 语义一致：平面是面，圆柱/样条等才是曲面。
   */
  return new FeatureFace(input);
}
